import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppMenuItem, AppModuleItem } from '../../../app.types';
import { AssetsService } from '../../../core/services/assets.service';

@Component({
  selector: 'app-menu',
  standalone: false,
  templateUrl: './app-menu.html',
  styleUrl: './app-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMenu implements OnInit, OnDestroy {
  app: AppModuleItem | null = null;
  openedItem: AppMenuItem | null = null;
  currentUrl = '';
  loading = false;
  error = '';
  private requestVersion = 0;
  private navigation?: Subscription;

  constructor(
    private readonly assets: AssetsService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    void this.loadApp();
    this.navigation = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      this.currentUrl = event.urlAfterRedirects;
      if (!this.app || !this.currentUrl.startsWith(this.normalizedRoot(this.app.route))) {
        void this.loadApp();
      } else if (this.vertical) {
        this.openActiveItem();
      }
      this.cdr.markForCheck();
    });
    this.navigation.add(this.assets.appVisibilityChanged.subscribe(() => void this.loadApp()));
  }

  ngOnDestroy(): void { this.requestVersion++; this.navigation?.unsubscribe(); }

  get vertical(): boolean { return this.normalizedRoot(this.currentUrl) === '/configuration'; }

  isActive(item: AppMenuItem): boolean {
    const path = this.currentUrl.split(/[?#]/)[0];
    const itemPath = this.linkPath(item.link);
    const queryMatches = Object.entries(this.linkQueryParams(item.link)).every(
      ([key, value]) => this.router.parseUrl(this.currentUrl).queryParams[key] === value
    );
    const matchesLink = !!itemPath && queryMatches && (
      path === itemPath || (path.startsWith(itemPath + '/') && !this.hasNestedMenuLink(itemPath))
    );
    return matchesLink
      || (item.children ?? []).some((child) => this.isActive(child));
  }

  private hasNestedMenuLink(path: string, items: AppMenuItem[] = this.app?.menu ?? []): boolean {
    return items.some((item) => {
      const itemPath = this.linkPath(item.link);
      return (!!itemPath && itemPath.startsWith(path + '/'))
        || this.hasNestedMenuLink(path, item.children ?? []);
    });
  }

  linkPath(link?: string | null): string | null {
    if (!link) return null;
    return link.split(/[?#]/)[0] || null;
  }

  linkQueryParams(link?: string | null): Record<string, string> {
    if (!link) return {};
    const query = link.split('?')[1]?.split('#')[0];
    if (!query) return {};
    return Object.fromEntries(new URLSearchParams(query).entries());
  }

  toggle(item: AppMenuItem, event: Event): void {
    if (!item.children?.length) return;
    event.preventDefault();
    this.openedItem = this.openedItem === item ? null : item;
  }

  close(): void {
    if (!this.vertical) this.openedItem = null;
  }

  trackItem(index: number, item: AppMenuItem): string { return item.link || `${index}:${item.name}`; }

  async loadApp(): Promise<void> {
    const version=++this.requestVersion;
    this.loading=true;this.error='';
    try {
      const app=await this.assets.getAppForRoute(this.currentUrl);
      if(version!==this.requestVersion)return;
      this.app=app;
      if(this.vertical)this.openActiveItem();
    } catch {
      if(version===this.requestVersion){this.app=null;this.error='Navigation could not be loaded.';}
    } finally {
      if(version===this.requestVersion){this.loading=false;this.cdr.detectChanges();}
    }
  }

  private openActiveItem(): void {
    this.openedItem = this.app?.menu.find((item) => this.isActive(item)) ?? null;
  }

  private normalizedRoot(route: string): string {
    const root = '/' + String(route || '').split(/[?#]/)[0].split('/').filter(Boolean)[0];
    return root === '/maintanance' ? '/maintenance' : root === '/project' ? '/projects' : root;
  }
}
